/**
 * Mansion-wide Gen Chat — satellites call DHC HQ /api/social/global-chat (no browser Supabase keys).
 * Sync via scripts/sync-mansion-chat.sh into CheepSweepz + CaribNavi.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { sanitizePublicCopy } from '../lib/public-copy';

function isOperationalChatRow(row) {
  const name = String(row?.user_name ?? '').trim();
  const text = String(row?.message ?? '').trim();
  if (!name && !text) return true;
  if (/^(launch|smoke|e2e|qa|test)(user|test|runner|bot)?$/i.test(name)) return true;
  if (/launchtest|smoketest|e2etest/i.test(name.replace(/[\s_-]+/g, ''))) return true;
  return /\bsmoke\s*test\b|\bapi\s+smoke\b|\blaunch\s*test\b|\be2e\s*test\b/i.test(text);
}

function filterPublicChatRows(rows) {
  return (Array.isArray(rows) ? rows : []).filter((row) => !isOperationalChatRow(row));
}

const ROOM_ID = 'main';
const CHAT_NAME_KEY = 'mansion-gen-chat-name';
const POLL_MS = 8000;

function resolveHqBase() {
  const backend = process.env.REACT_APP_BACKEND_URL || process.env.VITE_API_ENDPOINT || '';
  if (backend) {
    return backend.replace(/\/api\/?$/, '');
  }
  return (
    process.env.REACT_APP_DHC_HQ_URL ||
    process.env.REACT_APP_HQ_URL ||
    process.env.VITE_HQ_URL ||
    'https://digitalhustlerscaribbean.com'
  ).replace(/\/$/, '');
}

const HQ_BASE = resolveHqBase();
const CHAT_API = `${HQ_BASE}/api/social/global-chat`;

const SITE_ORIGIN =
  typeof window !== 'undefined' ? window.location.origin : 'https://mansion.local';

export default function MansionGenChat({
  userName: initialUserName = 'Guest',
  title = 'Mansion Chat',
  className = '',
  theme = 'dark',
}) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [userName, setUserName] = useState(initialUserName);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const latestIdRef = useRef(null);

  const isDark = theme !== 'light';

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const cached = window.localStorage.getItem(CHAT_NAME_KEY);
    if (cached) setUserName(cached);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(CHAT_NAME_KEY, userName);
  }, [userName]);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const url = `${CHAT_API}?room_id=${encodeURIComponent(ROOM_ID)}&limit=100`;
      const response = await fetch(url, {
        headers: { 'X-DHC-Hub': 'digitalhustlerscaribbean.com' },
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || `Chat unavailable (${response.status})`);
      }
      const rows = filterPublicChatRows(payload.messages);
      setMessages(rows);
      latestIdRef.current = rows[0]?.id ?? null;
      setError('');
    } catch (err) {
      setError(sanitizePublicCopy(err.message || 'Chat unavailable'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(() => load(true), POLL_MS);
    return () => window.clearInterval(timer);
  }, [load]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    if (isOperationalChatRow({ user_name: userName, message: text })) {
      setError('Test and operational messages cannot be posted to the public community chat.');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(CHAT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-DHC-Hub': 'digitalhustlerscaribbean.com',
          'X-Site-Origin': SITE_ORIGIN,
        },
        body: JSON.stringify({
          room_id: ROOM_ID,
          site_origin: SITE_ORIGIN,
          user_name: userName.trim() || 'Guest',
          message: text,
          business_id: null,
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || `Send failed (${response.status})`);
      }
      setInput('');
      setError('');
      if (payload.message && !isOperationalChatRow(payload.message)) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === payload.message.id)) return prev;
          return [payload.message, ...prev];
        });
      } else {
        await load(true);
      }
    } catch (err) {
      setError(sanitizePublicCopy(err.message || 'Unable to send message'));
    } finally {
      setSending(false);
    }
  };

  const shell = isDark
    ? 'border-slate-700/50 bg-slate-900/80 text-slate-200'
    : 'border-slate-200 bg-white text-slate-800 shadow-lg';
  const header = isDark ? 'border-slate-700/50 text-slate-400' : 'border-slate-200 text-slate-500';
  const inputCls = isDark
    ? 'bg-slate-800 border-slate-600 text-slate-200 placeholder-slate-500'
    : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400';
  const btn = isDark
    ? 'bg-orange-500 hover:bg-orange-600 text-slate-900'
    : 'bg-cyan-600 hover:bg-cyan-700 text-white';

  return (
    <div className={`flex flex-col rounded-xl border ${shell} ${className}`}>
      <div className={`flex items-center gap-2 border-b p-3 ${header}`}>
        <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
        <span className="ml-auto text-[10px] opacity-70">Live concierge chat</span>
      </div>

      <ul className="max-h-[320px] min-h-[200px] flex-1 divide-y divide-slate-700/20 overflow-y-auto p-3">
        {loading ? (
          <li className="py-4 text-center text-sm opacity-60">Loading…</li>
        ) : error ? (
          <li className="py-4 text-center text-sm text-red-400">{error}</li>
        ) : messages.length === 0 ? (
          <li className="py-4 text-center text-sm opacity-60">No messages yet. Say hi!</li>
        ) : (
          [...messages].reverse().map((m) => (
            <li key={m.id} className="py-2">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className={`text-sm font-medium ${isDark ? 'text-orange-300' : 'text-cyan-700'}`}>
                  {sanitizePublicCopy(m.user_name)}
                </span>
                <span className="text-xs opacity-50">
                  {new Date(m.created_at).toLocaleTimeString()}
                </span>
              </div>
              <p className="mt-0.5 break-words text-sm opacity-90">{sanitizePublicCopy(m.message)}</p>
            </li>
          ))
        )}
      </ul>

      <div className={`flex flex-col gap-2 border-t p-3 sm:flex-row ${header}`}>
        <input
          type="text"
          value={userName}
          onChange={(e) => setUserName(e.target.value)}
          placeholder="Your name"
          className={`w-full rounded-lg border px-3 py-2 text-sm sm:max-w-[140px] ${inputCls}`}
        />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
          placeholder="Type a message…"
          className={`flex-1 rounded-lg border px-3 py-2 text-sm ${inputCls}`}
        />
        <button
          type="button"
          onClick={send}
          disabled={sending || !input.trim()}
          className={`rounded-lg px-4 py-2 text-sm font-medium disabled:pointer-events-none disabled:opacity-50 ${btn}`}
        >
          {sending ? '…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
